import React, { useState, useEffect, RefObject, useRef } from 'react';
import { Grid, Grow, Box, Typography } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import { selectTitle, selectTitleDataById } from '../../../redux/selectors/titleSelectors';
import BrowserSeason from './BrowserSeason';
import BrowserItem from './BrowserItem';
import { useQuery } from '../../_hooks/useQuery';
import { sortBySequence } from '@/_utils/titleUtils';
import styled, { CSSObject } from '@emotion/styled';
import { TitleEntity } from '@/redux/slices/slice_types';
import { useAppSelector } from '@/redux/hooks';

const TitleDetailSeriesBrowser = () => {
  const params: Record<string, string> = useParams();
  const id = params.id;
  const title = useAppSelector(selectTitle);
  const titleDataById = useAppSelector(selectTitleDataById);
  const history = useHistory();
  const MAX_TRANSITION_TIME = 1000; // ms
  const query = useQuery();
  const seasonId = query.get('season_id');
  const episodeId = query.get('episode_id');
  const supplementalId = query.get('supplemental_id');
  const activeId = episodeId || supplementalId;
  const tab = query.get('tab') || 'metadata';
  const [seasonSelected, setSeasonSelected] = useState<any>(seasonId);
  const episodesExpanded = !!seasonId;
  const selectedSeason = seasonId ? titleDataById[seasonId] : null;
  const seasonChildren: TitleEntity[] = selectedSeason
    ? sortBySequence(selectedSeason?.children)?.filter((child: TitleEntity) => child.entity_type !== 'version') || []
    : [];

  const childrenRefs = useRef(seasonChildren.map(() => React.createRef()));


  if (!title) {
    // Just a type guard. Title should be already loaded when `TitleDetailSeriesBrowser` is rendered
    return null;
  }

  const seasons = title.children?.map((x: TitleEntity) => titleDataById[x.id]);

  const seasonRefs = seasons.reduce((acc: Record<string, RefObject<any>>, value: any, index: number) => {
    acc[index] = useRef();
    return acc;
  }, {});


  useEffect(() => {
    if (seasonId) {
        const flatSeasons = seasons.map((season: TitleEntity) => season.id);
        const seasonIndex = flatSeasons.indexOf(Number(seasonId));
        if (seasonIndex > -1) {
          setTimeout(() => {
            seasonRefs[seasonIndex]?.current?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          }, 100);
        }
    }
  }, [seasonRefs[0]?.current]);

  useEffect(() => {
    if (!activeId && childrenRefs.current[0]?.current) {
      setTimeout(() => {
        (childrenRefs.current[0]?.current as HTMLDivElement).scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }, 100);
    }
  }, [seasonId, childrenRefs.current[0]?.current]);

  useEffect(() => {
      if (activeId) {
        const flatEpisodes = seasonChildren.map((episode: TitleEntity) => episode.id);
        const episodeIndex = flatEpisodes.indexOf(Number(activeId));
        setTimeout(() => {
          (childrenRefs.current[episodeIndex]?.current as HTMLDivElement).scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 100);
      }
  }, [childrenRefs.current[0]]);

  const handleSeasonClick = (seasonId: string) => {
    history.push(`/titles/${id}?tab=${tab}&season_id=${seasonId}`);
    setSeasonSelected(true);
  };

  const handleEpisodeClick = (entity: TitleEntity) => {
    history.push(`/titles/${id}?tab=${tab}&season_id=${entity.season_id}&episode_id=${entity.id}`);
  };

  const renderEpisodes = () => {
    if (!seasonId || !episodesExpanded || (seasonChildren && seasonChildren?.length === 0)) {
      return <></>;
    }

    return (
      <EpisodesOverflow>
        <SeasonEpisodes style={{ maxHeight: '350px', overflowY: 'auto' }}>
          {seasonChildren?.map((entity, i) => (
            <OptionalFadeIn key={entity.id} ref={childrenRefs.current[i] as RefObject<HTMLDivElement>} timeout={activeId ? null : (i < 10 ? i * (MAX_TRANSITION_TIME / 10) : null)}>
              <BrowserItem sx={{ mb: 1 }} onClick={() => handleEpisodeClick(entity)} active={activeId ? parseInt(activeId) === entity.id : false}>
                <Grid container alignItems="center" sx={{ ml: 1 }}>
                  {entity.entity_type === 'episode' && (
                    <Typography variant="subtitle2" sx={{ mr: 1 }}>
                      E{entity.sequence_order}:{' '}
                    </Typography>
                  )}
                  <span>{entity.name}</span>
                </Grid>
              </BrowserItem>
            </OptionalFadeIn>
          ))}
        </SeasonEpisodes>
      </EpisodesOverflow>
    );
  };

  return (
    <BoxContainer>
      <Grid container columnSpacing={4}>
        <Grid item xs={12} sm={6}>
          <SeasonsList>
            {seasons.map((season: TitleEntity, i: number) => (
              <OptionalFadeIn ref={seasonRefs[i]} key={season.id} timeout={seasonId ? null : (i < 10 ? i * (MAX_TRANSITION_TIME / 10) : null)}>
                <BrowserSeason season={season} episodeSelected={Number(seasonId) === season.id && episodeId} onSeasonClick={handleSeasonClick} />
              </OptionalFadeIn>
            ))}
          </SeasonsList>
        </Grid>

        <Grid item xs={12} sm={6} sx={{ flex: 1.25, position: 'relative' }}>
          {seasonSelected && renderEpisodes()}
        </Grid>
      </Grid>
    </BoxContainer>
  );
};

export default TitleDetailSeriesBrowser;

const BoxContainer = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

const SeasonsList = styled.div`
  position: relative;
  overflow: auto;
  max-height: 350px;
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.spacing(1)};
`;

const EpisodesOverflow = styled.div`
  padding-bottom: 40px;
  &:before {
    content: '';
    height: 50px;
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    background: ${({ theme }) => {
      const overrides = theme.components?.MuiPaper?.styleOverrides?.root as CSSObject;
      return `linear-gradient(transparent, ${overrides.backgroundColor || theme.palette.background.paper} 50%)`;
    }};
    width: 100%;
    z-index: 99;
  }
`;

const SeasonEpisodes = styled.div`
  padding-right: ${({ theme }) => theme.spacing(1)};
  max-height: 360px;
  position: relative;
  overflow: auto;
`;

const OptionalFadeIn = styled.div<{ timeout: number | null }>`
  opacity: ${({ timeout }) => (timeout === null ? 1 : 0)};
  animation: fadeInAnimation 0.5s forwards;
  animation-delay: ${({ timeout }) => timeout ?? 0}ms;

  @keyframes fadeInAnimation {
    to {
      opacity: 1;
    }
  }
`;